import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { useGetCartQuery } from '@/components/orders/services/orderApi';
import { useAddPropertiesToCart } from '@/components/properties/property-view/hooks/useAddPropertiesToCart';
import type { PropertyEntity } from '@/types/property.types';
import { useNewOrderDrawerState } from './hooks/useNewOrderDrawerState';
import { AddToCartBanner } from './components/AddToCartBanner';
import { PropertyPickerView } from './components/PropertyPickerView';
import { AddNewPropertyView } from './components/AddNewPropertyView';

interface NewOrderDrawerProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

const formatAddress = (p: PropertyEntity) =>
  `${p.address1} · ${p.city}, ${p.state} ${p.zipCode}`;

export function NewOrderDrawer({ open, onOpenChange }: NewOrderDrawerProps) {
  const drawer = useNewOrderDrawerState();
  const addToCart = useAddPropertiesToCart();
  const userId = localStorage.getItem('userId') ?? '';
  const { data: cart } = useGetCartQuery(userId, { skip: !userId });
  const cartCount = cart?.items?.length ?? 0;
  const propertyIdsInCart = useMemo(
    () => new Set((cart?.items ?? []).map((item) => item.propertyId)),
    [cart?.items],
  );

  const [addingPropertyId, setAddingPropertyId] = useState<string | null>(null);

  // Tracks Formik dirty state inside AddNewPropertyView so we can prompt
  // before Esc/overlay/back-arrow discards in-flight edits. A ref (not
  // state) so updating it from the form's effect doesn't re-render the
  // drawer.
  const formDirtyRef = useRef(false);

  // null when no prompt is up; 'close' means the user tried to close the
  // drawer, 'back' means they tried to return to picker view. Routes the
  // Discard action to the right behavior.
  const [discardIntent, setDiscardIntent] = useState<null | 'close' | 'back'>(
    null
  );

  // Sync external open prop with the internal state machine. Closing
  // resets the state machine; opening (re)enters picker view. Wrapped in
  // useEffect to avoid the setState-during-render anti-pattern under
  // StrictMode. `drawer` is stable from the hook (its returned functions
  // are useCallback'd), so depending on it does not loop.
  useEffect(() => {
    if (open && !drawer.isOpen) drawer.openDrawer();
    if (!open && drawer.isOpen) drawer.closeDrawer();
  }, [open, drawer]);

  // Reset the dirty-tracking ref any time we leave the add-new view, so
  // a stale "dirty" from a previous edit can't gate a future close.
  useEffect(() => {
    if (drawer.view !== 'addNew') formDirtyRef.current = false;
  }, [drawer.view]);

  const handleSheetOpenChange = (next: boolean) => {
    if (!next && drawer.view === 'addNew' && formDirtyRef.current) {
      setDiscardIntent('close');
      return;
    }
    onOpenChange(next);
    if (!next) drawer.closeDrawer();
  };

  const requestBack = useCallback(() => {
    if (formDirtyRef.current) {
      setDiscardIntent('back');
      return;
    }
    drawer.backToPicker();
  }, [drawer]);

  const confirmDiscard = () => {
    formDirtyRef.current = false;
    const intent = discardIntent;
    setDiscardIntent(null);
    if (intent === 'close') {
      onOpenChange(false);
      drawer.closeDrawer();
    } else if (intent === 'back') {
      drawer.backToPicker();
    }
  };

  const handleAddToCart = useCallback(
    async (property: PropertyEntity) => {
      setAddingPropertyId(property.propId);
      try {
        await addToCart([property.propId]);
        drawer.showSuccessBanner('Added to cart', formatAddress(property));
      } catch (err) {
        drawer.showErrorBanner(
          "Couldn't add to cart",
          err instanceof Error ? err.message : 'Unknown error',
          {
            propertyId: property.propId,
            propertyAddress: formatAddress(property),
            createdNewProperty: false,
          }
        );
      } finally {
        setAddingPropertyId(null);
      }
    },
    [addToCart, drawer]
  );

  const handleRetry = useCallback(
    async (retry: {
      propertyId: string;
      propertyAddress: string;
      createdNewProperty: boolean;
    }) => {
      if (!retry.propertyId) return;
      setAddingPropertyId(retry.propertyId);
      try {
        await addToCart([retry.propertyId]);
        drawer.showSuccessBanner('Added to cart', retry.propertyAddress);
      } catch (err) {
        drawer.showErrorBanner(
          "Couldn't add to cart",
          err instanceof Error ? err.message : 'Unknown error',
          retry
        );
      } finally {
        setAddingPropertyId(null);
      }
    },
    [addToCart, drawer]
  );

  // One banner instance shared by both views — rendered above the
  // picker rows in picker view, above the form in add-new view.
  // Centralising this is what lets a create-failure leave the user on
  // the form with their data intact (the banner appears in-place
  // instead of forcing a navigation back to the picker).
  const bannerNode = drawer.banner ? (
    <div className="mb-4">
      <AddToCartBanner
        banner={drawer.banner}
        onDismiss={drawer.dismissBanner}
        onRetry={handleRetry}
      />
    </div>
  ) : null;

  return (
    <>
      <Sheet open={open} onOpenChange={handleSheetOpenChange}>
        <SheetContent
          side="right"
          // Drawer width is purpose-tuned: picker/add-new sits at 560 px,
          // wider than a filters panel (~480) but well below the services
          // drawer's ~900 px. Bump per-drawer if a new flow needs more
          // room rather than promoting one default.
          className="flex w-full flex-col gap-0 p-0 sm:max-w-[560px] [&>button]:hidden"
          // The add-new-property form's Google Places Autocomplete renders
          // its suggestion list (`.pac-container`) on document.body, outside
          // this dialog's subtree. Two things break mouse-selecting a
          // suggestion, and both must be handled:
          //   1. A modal dialog disables pointer events on <body>, so the
          //      body-level pac-container is unclickable — fixed globally by
          //      the `.pac-container { pointer-events: auto }` rule in app.css.
          //   2. The resulting pointer-down lands outside this content, so
          //      Radix's dismiss layer would tear the drawer down before
          //      Google's place selection fires — prevented here by ignoring
          //      outside pointer-downs that originate in the pac-container.
          // (onPointerDownOutside alone suppresses the dismiss; Radix only
          // calls onDismiss when the event isn't defaultPrevented.)
          onPointerDownOutside={(e) => {
            const target = e.detail.originalEvent.target as Element | null;
            if (target?.closest('.pac-container')) e.preventDefault();
          }}
        >
          {drawer.view === 'picker' ? (
            <>
              <div className="flex items-start justify-between gap-3 border-b border-border px-8 py-6 max-lg:px-5">
                <div className="flex-1">
                  <SheetTitle className="text-2xl">
                    Choose a Property to Add to the Cart
                  </SheetTitle>
                  <Typography className="mt-1 text-sm text-muted-foreground">
                    Search your saved properties, or add a new one.
                  </Typography>
                </div>
                <SheetClose asChild>
                  <button
                    type="button"
                    aria-label="Close"
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 max-lg:h-11 max-lg:w-11"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </SheetClose>
              </div>

              <div className="flex-1 overflow-y-auto px-8 py-8 max-lg:px-5">
                {bannerNode}
                <PropertyPickerView
                  onAddToCart={handleAddToCart}
                  onAddNewProperty={drawer.goToAddNew}
                  addingPropertyId={addingPropertyId}
                  propertyIdsInCart={propertyIdsInCart}
                  cartCount={cartCount}
                />
              </div>
            </>
          ) : (
            <AddNewPropertyView
              onBack={requestBack}
              bannerNode={bannerNode}
              onDirtyChange={(dirty) => {
                formDirtyRef.current = dirty;
              }}
              onCreated={(property) => {
                formDirtyRef.current = false;
                drawer.showSuccessBanner(
                  'Property created & added to cart',
                  formatAddress(property)
                );
              }}
              onError={(title, detail, retry) =>
                drawer.showErrorBanner(title, detail, retry)
              }
            />
          )}
        </SheetContent>
      </Sheet>

      <Modal
        open={discardIntent !== null}
        onOpenChange={(next) => {
          if (!next) setDiscardIntent(null);
        }}
        title="Discard Changes?"
        description="You have unsaved property details. Closing this form will discard them."
        size="sm"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setDiscardIntent(null)}
            >
              Keep Editing
            </Button>
            <Button variant="destructive" onClick={confirmDiscard}>
              Discard
            </Button>
          </>
        }
      />
    </>
  );
}
